import { AxiosResponse } from "axios";
import { AxiosHelper } from "../axiosConfig";
import APIConstant from "../utils/apiConstant";
import { IAccount } from "../model/interface/user";
import {
  IDistrictOption,
  IRosterConnectionCounts,
} from "../model/interface/districts";
import {
  ICanvasSchool,
  ICanvasSchoolRequest,
} from "../model/interface/ICanvasSchool";
import { IResult } from "../model/interface/result";
import { ICanvasAccountSettings } from "../model/interface/ICanvasAccountSettings";

export const getMiniCRMUsers = (params) => {
  return AxiosHelper.axiosInstance().get(APIConstant.CMS_MINI_CRM_USERS, {
    params: params,
  });
};

export const exportMiniCRMUsers = (params) => {
  return AxiosHelper.axiosInstance().get(
    APIConstant.CMS_MINI_CRM_USERS_EXPORT,
    {
      responseType: "blob",
      params: params,
    }
  );
};

export const getMiniCRMSchools = (params) => {
  return AxiosHelper.axiosInstance().get(APIConstant.CMS_MINI_CRM_SCHOOLS, {
    params: {
      StateId: params.stateId,
      Region: params.region,
      DistrictId: params.districtId,
      DistrictName: params.districtName,
      SchoolName: params.schoolName,
      OrderByColumn: params.orderByColumn,
      SortDir: params.sortDir,
      PageSize: params.pageSize,
      PageNumber: params.pageNumber,
      SchoolId: params.schoolId ?? 0,
      city: params.city,
    },
  });
};

export const getMiniCRMNWEASchools = (params) => {
  return AxiosHelper.axiosInstance().get(
    APIConstant.CMS_MINI_CRM_NWEA_SCHOOLS,
    {
      params: {
        SchoolName: params.schoolName,
        PageSize: params.pageSize,
        PageNumber: params.pageNumber,
      },
    }
  );
};

export const getMiniCRMDistricts = (params) => {
  return AxiosHelper.axiosInstance().get(APIConstant.CMS_MINI_CRM_DISTRICTS, {
    params: {
      StateId: params.stateId,
      Region: params.region,
      DistrictId: params.districtId,
      DistrictName: params.districtName,
      City: params.city,
      OrderByColumn: params.orderByColumn,
      SortDir: params.sortDir,
      PageSize: params.pageSize,
      PageNumber: params.pageNumber,
    },
  });
};

export const getMiniCRMDistrictOptions = async (
  stateId = 0
): Promise<Array<IDistrictOption>> => {
  const resp = await getMiniCRMDistricts({
    stateId,
    region: "",
    districtId: 0,
    districtName: "",
    orderByColumn: "Name",
    sortDir: "ASC",
    pageSize: 99999,
    pageNumber: 1,
  });
  return (resp?.data?.data ?? []).map((ds) => ({
    label: ds.name,
    value: ds.districtId,
    stateId: ds.stateId,
    stateName: ds.stateName,
  }));
};

export const updateUserRole = (userId, roleId) => {
  return AxiosHelper.axiosInstance().put(APIConstant.UPDATE_USER_ROLE, {
    userId,
    roleId,
    updateByUserId: userId,
  });
};

export const addUser = (params) => {
  return AxiosHelper.axiosInstance().post(
    APIConstant.CMS_MINI_CRM_USERS_CREATE,
    params
  );
};
export const addSchoolAdmin = (params) => {
  return AxiosHelper.axiosInstance().post(APIConstant.APP_USER_ADD, params);
};

export const updateUser = (params) => {
  return AxiosHelper.axiosInstance().put(
    APIConstant.CMS_MINI_CRM_USERS_UPDATE,
    params
  );
};

export const updatePassword = (params) => {
  return AxiosHelper.axiosInstance().put(
    APIConstant.CMS_MINI_CRM_USERS_UPDATE,
    params
  );
};

export const deleteUserById = (userId: number) => {
  return AxiosHelper.axiosInstance().delete(
    APIConstant.CMS_MINI_CRM_USERS_DELETE.replace("{userId}", userId.toString())
  );
};

export const addSchool = (params) => {
  return AxiosHelper.axiosInstance().post(
    APIConstant.CMS_MINI_CRM_USERS_CREATE,
    params
  );
};

export const updateSchool = (params) => {
  return AxiosHelper.axiosInstance().put(
    APIConstant.CMS_MINI_CRM_SCHOOLS_UPDATE,
    params
  );
};

export const deleteSchoolById = (params) => {
  return AxiosHelper.axiosInstance().delete(
    APIConstant.CMS_MINI_CRM_SCHOOLS_DELETE,
    params
  );
};

export const addDistrict = (params) => {
  return AxiosHelper.axiosInstance().post(
    APIConstant.CMS_MINI_CRM_USERS_CREATE,
    params
  );
};

export const updateDistrict = (params) => {
  return AxiosHelper.axiosInstance().put(
    APIConstant.CMS_MINI_CRM_DISTRICTS_UPDATE,
    params
  );
};

export const deleteDistrictById = (params) => {
  return AxiosHelper.axiosInstance().put(
    APIConstant.CMS_MINI_CRM_DISTRICTS_DELETE,
    params
  );
};

export const getCanvasAccountSettingsByDistrictId = (districtId) => {
  return AxiosHelper.axiosInstance().get<IResult<ICanvasAccountSettings[]>>(
    APIConstant.CMS_MINI_CRM_CANVAS_ACCOUNTSETTINGS,
    {
      params: {
        districtId: districtId,
      },
    }
  );
};

export const getCanvasAccountSchoolsByDistrictId = (districtId: number) => {
  return AxiosHelper.axiosInstance().get<IResult<ICanvasSchool[]>>(
    APIConstant.CMS_MINI_CRM_CANVAS_DISTRICTSCHOOLS,
    {
      params: {
        districtId: districtId,
      },
    }
  );
};

export const updateCanvasAccountSchools = (schools: ICanvasSchoolRequest[]) => {
  return AxiosHelper.axiosInstance().post<IResult<boolean>>(
    APIConstant.CMS_MINI_CRM_CANVAS_SCHOOLS,
    { schools }
  );
};

export const addCanvasAccountSettings = (params) => {
  return AxiosHelper.axiosInstance().post(
    APIConstant.CMS_MINI_CRM_CANVAS_CREATE_ACCOUNTSETTINGS,
    params
  );
};

export const updateCanvasAccountSettings = (params) => {
  return AxiosHelper.axiosInstance().put(
    APIConstant.CMS_MINI_CRM_CANVAS_UPDATE_ACCOUNTSETTINGS,
    params
  );
};

export const syncDistrict = (params) => {
  return AxiosHelper.axiosInstance().post(
    APIConstant.CMS_MINI_CRM_DISTRICT_SYNC,
    params
  );
};

export function fetchAccountDetail(
  accountLoginName: string,
  email: string
): Promise<AxiosResponse<IAccount>> {
  return AxiosHelper.axiosInstance().post(
    APIConstant.ForgotPassword_API.GetAccountDetail,
    {
      accountLoginName: accountLoginName,
      email: email,
    }
  );
}

export const getDistrictRosterConnectionCounts = (
  districtId: number
): Promise<AxiosResponse<Array<IRosterConnectionCounts>>> => {
  return AxiosHelper.axiosInstance().get(
    APIConstant.CMS_MINI_CRM_DISTRICTS_GET_ROSTER_CONNECTION_COUNTS.replace(
      "{districtId}",
      districtId.toString()
    )
  );
};

export const deleteDistrictRosterConnections = (
  districtId: number,
  externalRosterProviderId: number
): Promise<AxiosResponse<number>> => {
  return AxiosHelper.axiosInstance().delete(
    APIConstant.CMS_MINI_CRM_DISTRICTS_DELETE_ROSTER_CONNECTIONS.replace(
      "{districtId}",
      districtId.toString()
    ),
    {
      params: {
        externalRosterProviderId: externalRosterProviderId,
      },
    }
  );
};

export const ReActivateUser = (
  userId: number
): Promise<AxiosResponse<number>> => {
  return AxiosHelper.axiosInstance().put(
    APIConstant.CMS_MINI_CRM_USERS_REACTIVATE.replace(
      "{userId}",
      userId.toString()
    )
  );
};
